/**
 * First-touch attribution capture (CEL-491/CEL-526, approved Jul 24 2026).
 *
 * On the visitor's FIRST pageview we store UTMs + referrer + landing path in localStorage.
 * First touch wins: once stored, never overwritten — "what first sold them" is the question.
 * At signup, AuthContext sends this as the X-CellCog-First-Touch header on /session/login;
 * the backend stamps it onto the User once (immutable; 48h account-age guard server-side).
 *
 * Ad attribution (Sep 27 2026, Nitish; legal review before deploy):
 *  - utm_term / utm_content are captured like the other UTMs.
 *  - Google Ads click IDs (gclid, gbraid, wbraid, gad_source) are held IN PAGE MEMORY ONLY
 *    (a module variable, never localStorage/sessionStorage/cookies: EU ePrivacy treats any
 *    device storage like a cookie) until consent is known. CookieConsentContext then calls
 *    commitAdClickIds (outside the opt-in regions with no rejection/GPC, or after an explicit
 *    accept) or clearAdClickIds (rejection / GPC). A full reload before a choice loses them,
 *    by design. Click IDs keep their case.
 *
 * Privacy: first-party storage only, no third-party trackers, nothing sent to Google from
 * here. Untagged direct arrivals may store only referrer/landing_path.
 */

const FIRST_TOUCH_KEY = 'cellcog_first_touch';
export const AD_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid', 'gad_source'];
// Blog voice (CEL-3260, contract §6): `vs` = the blog call's session id (a 24-hex ObjectId). Read on ANY landing; it joins the
// stored record once (write-once, like the record itself) even when the first touch was an earlier visit, because the call is
// what we want to join to the signup. Anything that is not exactly 24 hex characters is ignored.
const BLOG_VOICE_SESSION_RE = /^[0-9a-f]{24}$/i;

/** The blog voice session id as stored (lowercase), or null. */
export function normalizeBlogVoiceSessionId(raw) {
  const v = typeof raw === 'string' ? raw.trim() : '';
  return BLOG_VOICE_SESSION_RE.test(v) ? v.toLowerCase() : null;
}

// Held click IDs awaiting a consent decision. Memory only; see header.
let pendingAdClickIds = null;

/** Capture on app boot. Safe to call on every pageview — no-ops after the first (except a first `vs`, see above). */
export function captureFirstTouch() {
  try {
    const params = new URLSearchParams(window.location.search);
    const vs = normalizeBlogVoiceSessionId(params.get('vs'));
    const stored = localStorage.getItem(FIRST_TOUCH_KEY);
    if (stored) {
      // first touch wins; only a first blog voice session id may still join it
      if (vs) {
        const ft = JSON.parse(stored);
        if (ft && typeof ft === 'object' && !ft.vs) localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ ...ft, vs }));
      }
      return;
    }

    const data = {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_term: params.get('utm_term') || null,
      utm_content: params.get('utm_content') || null,
      referrer: document.referrer ? document.referrer.slice(0, 512) : null,
      landing_path: window.location.pathname.slice(0, 256),
      captured_at: new Date().toISOString(),
    };
    if (vs) data.vs = vs;

    // Internal navigation (referrer = ourselves) with no UTMs isn't a meaningful first touch
    // signal loss — but landing_path + external referrer are worth keeping even without UTMs.
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(data));

    const ad = {};
    AD_CLICK_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) ad[k] = v.slice(0, 256);
    });
    pendingAdClickIds = Object.keys(ad).length ? ad : null;
  } catch (e) {
    // Fail-soft: attribution must never break the app (e.g. localStorage disabled).
  }
}

/** Consent allows ad attribution: merge held click IDs into the first-touch record. */
export function commitAdClickIds() {
  try {
    if (!pendingAdClickIds) return;
    const ft = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || 'null');
    if (!ft) return;
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ ...ft, ...pendingAdClickIds }));
    pendingAdClickIds = null;
  } catch (e) {
    // fail-soft
  }
}

/** Rejection / GPC: drop held click IDs and remove any already merged. */
export function clearAdClickIds() {
  try {
    pendingAdClickIds = null;
    const ft = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || 'null');
    if (!ft) return;
    AD_CLICK_KEYS.forEach((k) => { delete ft[k]; });
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(ft));
  } catch (e) {
    // fail-soft
  }
}

/** Header value for /session/login, or null if nothing captured. */
export function getFirstTouchHeader() {
  try {
    return localStorage.getItem(FIRST_TOUCH_KEY) || null;
  } catch (e) {
    return null;
  }
}
